import { apiClient } from '@/api/client';
import type {
  CommunityOption,
  SpringPage,
  StudentDetail,
  StudentListParams,
  StudentSummary,
} from '@/types/students';

/**
 * Communities that the admin UI should NOT show — typically the system-default
 * community every user is auto-added to (config: VITE_DEFAULT_COMMUNITY_ID).
 * CSV-aware: supports "26,27,28" for hiding multiple.
 */
const HIDDEN_COMMUNITY_IDS: ReadonlySet<number> = (() => {
  const raw = import.meta.env.VITE_DEFAULT_COMMUNITY_ID ?? '';
  const ids = raw
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
  return new Set(ids);
})();

export function isHiddenCommunity(
  communityId: number | null | undefined,
): boolean {
  return (
    communityId !== null &&
    communityId !== undefined &&
    HIDDEN_COMMUNITY_IDS.has(communityId)
  );
}

function buildQuery(params: StudentListParams): URLSearchParams {
  const query = new URLSearchParams();
  if (params.communityId !== undefined && params.communityId !== null) {
    query.set('communityId', String(params.communityId));
  }
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.size !== undefined) query.set('size', String(params.size));
  if (params.sort) query.set('sort', params.sort);
  if (params.search) query.set('search', params.search);
  if (params.status) query.set('status', params.status);
  if (params.communityRole) query.set('communityRole', params.communityRole);
  if (params.cohort !== undefined && params.cohort !== null) {
    query.set('cohort', String(params.cohort));
  }
  if (params.includeInactive) query.set('includeInactive', 'true');
  return query;
}

export async function listStudents(
  params: StudentListParams,
): Promise<SpringPage<StudentSummary>> {
  const { data } = await apiClient.get<SpringPage<StudentSummary>>(
    `/setting/students`,
    { params: buildQuery(params) },
  );
  return data;
}

export async function getStudent(userId: number): Promise<StudentDetail> {
  const { data } = await apiClient.get<StudentDetail>(
    `/setting/students/${userId}`,
  );
  return data;
}

export async function listCommunities(): Promise<CommunityOption[]> {
  const { data } = await apiClient.get<CommunityOption[]>('/setting/communities');
  return data;
}
